<script setup lang="ts">
import { computed, onBeforeMount, onUpdated, ref } from 'vue'

import clsx from 'clsx'
import { ColorPicker } from 'vue3-colorpicker'
import { useEyeDropper } from '@vueuse/core'

import { useLayoutStore } from '@/stores/layout'

const { isSupported, open } = useEyeDropper()
const layoutStore = useLayoutStore()

const colors = computed(() => layoutStore.getColors())

const props = withDefaults(defineProps<{
    label: string
    value?: string
    defaultValue?: string
    clearOnDefault?: boolean
}>(), {
    value: '',
    defaultValue: '',
    clearOnDefault: false,
})

const emit = defineEmits<{
    change: [value: string]
}>()

const activeValue = ref('')

const defaultActionValue = computed(() => {
    return props.clearOnDefault ? '' : props.defaultValue
})

const onClickColorPicker = async () => {
    const result = await open()

    if (!result) {
        return
    }

    activeValue.value = result.sRGBHex?.toUpperCase() ?? ''

    emit('change', activeValue.value)
}

const onChangeColor = (color: string) => {
    if (color.length === 9 && color[color.length - 1] === 'f' && color[color.length - 2] === 'f') {
        color = color.slice(0, -2)
    }

    if (color === '#00000000') {
        onChangeColor(defaultActionValue.value)
        emit('change', activeValue.value)
        return
    }

    activeValue.value = color.toUpperCase()

    emit('change', activeValue.value)
}

const isCustomColor = computed(() => {
    const filtered = colors.value.filter(
        (color: any) => color.value.toUpperCase() === activeValue.value
    )

    return filtered.length === 0
})

onBeforeMount(() => {
    if (props.value) {
        activeValue.value = props.value.toUpperCase()
        return
    }

    if (!props.value && props.defaultValue) {
        activeValue.value = props.defaultValue
    }
})

onUpdated(() => {
    if (activeValue.value !== '') {
        return
    }

    if (activeValue.value == null) {
        activeValue.value = props.defaultValue
        return
    }

    activeValue.value = props.value?.toUpperCase() ?? ''
})
</script>

<template>
    <div class="beta-color-picker">
        <span class="beta-color-picker__label">
            {{ props.label }}
        </span>

        <div class="beta-color-picker__palette">
            <button
                v-if="props.defaultValue !== ''"
                class="beta-color-picker__swatch-row"
                @click="onChangeColor(defaultActionValue)"
            >
                <span>PADRÃO</span>

                <div
                    :class="
                        clsx(
                            props.defaultValue.toUpperCase() === activeValue.toUpperCase()
                                ? 'border-[var(--main-color)]'
                                : 'border-[#EEEEEE]',
                            'beta-color-picker__swatch'
                        )
                    "
                    :style="{ backgroundColor: props.defaultValue }"
                ></div>
            </button>

            <template v-for="(color, index) in colors" :key="index">
                <button
                    v-if="props.defaultValue.toUpperCase() !== color.value.toUpperCase()"
                    class="beta-color-picker__swatch-row"
                    @click="onChangeColor(color.value)"
                >
                    <span>
                        {{ color.value.toUpperCase() }}
                    </span>

                    <div
                        v-tooltip="{
                            text: color.name.toUpperCase(),
                            position: 'left'
                        }"
                        :class="
                            clsx(
                                color.value.toUpperCase() === activeValue.toUpperCase()
                                    ? 'border-[var(--main-color)]'
                                    : 'border-[#EEEEEE]',
                                'beta-color-picker__swatch'
                            )
                        "
                        :style="{ backgroundColor: color.value }"
                    ></div>
                </button>
            </template>
        </div>

        <div
            :class="
                clsx(
                    'beta-color-picker__custom',
                    isCustomColor
                        ? 'border-[var(--main-color)]'
                        : 'border-[#F5F5F5]'
                )
            "
        >
            <input
                v-model="activeValue"
                type="text"
                class="beta-color-picker__input"
                @input="(event: any) => onChangeColor(event.target.value)"
            />

            <div class="beta-color-picker__actions">
                <color-picker
                    format="hex8"
                    shape="circle"
                    :pure-color="activeValue"
                    @pure-color-change="onChangeColor"
                />

                <button v-if="isSupported" class="beta-color-picker__eyedropper" @click="onClickColorPicker">
                    <span class="sr-only">Color Picker</span>

                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                    >
                        <g opacity="0.1">
                            <path
                                d="M7.85463 4.95537L13.0445 10.1452M1.7998 12.7401L12.0233 2.51662C12.9788 1.56119 14.5278 1.56119 15.4832 2.51662C16.4387 3.47204 16.4387 5.02109 15.4832 5.97652L5.25971 16.2H1.7998V12.7401Z"
                                stroke="#060606"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </g>
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.beta-color-picker {
    @apply space-y-2;
}

.beta-color-picker__label {
    @apply text-sm font-semibold text-[#060606] opacity-50;
}

.beta-color-picker__palette {
    @apply h-24 w-full space-y-2 overflow-y-auto pb-2 pr-2;
}

.beta-color-picker__swatch-row {
    @apply flex min-h-[22px] w-full items-center justify-between gap-3 text-sm font-bold text-[#060606];
}

.beta-color-picker__swatch-row span {
    @apply opacity-30;
}

.beta-color-picker__swatch {
    @apply h-[18px] w-[18px] shrink-0 rounded-full border-[1.5px];
}

.beta-color-picker__custom {
    @apply flex min-h-[54px] w-full items-center gap-3 rounded-xl border-2 bg-[#FCFCFC] px-4 py-3;
}

.beta-color-picker__input {
    @apply min-w-0 flex-1 border-0 bg-transparent p-0 text-sm font-bold text-[#060606] opacity-30 outline-none;
}

.beta-color-picker__actions {
    @apply flex shrink-0 items-center gap-2;
}

.beta-color-picker__eyedropper {
    @apply flex h-[18px] w-[18px] shrink-0 items-center justify-center;
}

:deep(.vc-color-wrap) {
    @apply h-[18px] w-[18px] shrink-0 shadow-none;
}
</style>
